import clsx from "clsx";
import React from "react";
import {IBucketLifecyclePolicy} from "api/entities/bucket";
import {FormFieldLabel} from "components/shared-ui/form/form-field-label/form-field-label";
import {FormFieldValue} from "components/shared-ui/form/form-field-value/form-field-value";
import {FormField} from "components/shared-ui/form/form-field/form-field";
import Checkbox from "components/shared-ui/input/checkbox";
import SidePanelSection from "components/shared-ui/side-panel-section/side-panel-section";
import TooltipQuestion from "components/shared-ui/tooltip-question";
import styles from "./retention-section-v2.module.scss";
import TimeIntervalSelector from "../time-inverval-selector/time-interval-selector";

export const defaultRetentionSettingV2: IRetentionSettingsV2 = {
  deleteFiles: false,
  retentionDays: null,
};

export interface IRetentionSettingsV2Errors {
  deleteFiles?: string;
  retentionDays?: string;
}

export interface IRetentionSettingsV2 {
  deleteFiles: boolean;
  retentionDays?: number;
}

export function getRetentionSettings(bucketConfig: IBucketLifecyclePolicy): IRetentionSettingsV2 {
  return {
    deleteFiles: !!bucketConfig.retentionDays,
    retentionDays: bucketConfig.retentionDays
  };
}

const REVISIONS_TIME_TOOLTIP =
    "Myota will permanently delete your file version history once it exceed the allotted timeframe.";

interface IRetentionSectionV2Props {
  autoFocus?: boolean;
  retentionSettings: IRetentionSettingsV2;
  disabled?: boolean;
  onChange: (value: IRetentionSettingsV2) => void;
  errors: IRetentionSettingsV2Errors;
  onError: (errors: Partial<IRetentionSettingsV2Errors>) => void;
}

export function RetentionSectionV2(props: IRetentionSectionV2Props) {
  const retention = props.retentionSettings;

  function onFieldChanged(value: Partial<IRetentionSettingsV2>) {
    const newSettings = {...props.retentionSettings, ...value};
    props.onChange(newSettings);
    validateField(Object.keys(value)[0] as keyof IRetentionSettingsV2, value);
  }

  function validateField(
    fieldName: keyof IRetentionSettingsV2,
    value: Partial<IRetentionSettingsV2>
  ) {
    switch (fieldName) {
      case "retentionDays":
        props.onError({
          [fieldName]:
            value[fieldName] <= 0
              ? "Entered value must be a positive integer"
              : null,
        });
    }
  }

  return (
    <SidePanelSection
      title="Lifecycle Policy"
      testId="storage-retention"
      className={clsx(styles.root, styles.input)}
    >
      <FormField testId="deletion-retention">
        <FormFieldLabel
          data-testid="storage-retention-description"
          noLeftMargin={true}
        >
          How would you like to handle a user’s file deletion?
          <TooltipQuestion>
            When a user deletes files, Myota can allow them to recover these
            files. Parameters for permanent deletion can be set.
          </TooltipQuestion>
        </FormFieldLabel>

        <FormField
          className={clsx(
            styles["retention-param"],
            !retention.deleteFiles &&
              styles["retention-param--disabled"]
          )}
        >
          <FormFieldLabel className={styles["retention-param-label"]}>
            <Checkbox
              testId="retention-age"
              disabled={props.disabled}
              label="Delete files:"
              checked={retention.deleteFiles}
              onChange={value =>
                onFieldChanged({
                  deleteFiles: value,
                })
              }
            />
            <TooltipQuestion>{REVISIONS_TIME_TOOLTIP}</TooltipQuestion>
          </FormFieldLabel>
          <FormFieldValue className={styles["retention-param-value"]}>
            <TimeIntervalSelector
              tooltip={REVISIONS_TIME_TOOLTIP}
              value={retention.retentionDays}
              disabled={props.disabled || !retention.deleteFiles}
              onChange={retentionDays =>
                onFieldChanged({ retentionDays })
              }
            />
            {props.errors.retentionDays && (
              <div className={styles["error-text"]}>
                {props.errors.retentionDays}
              </div>
            )}
          </FormFieldValue>
        </FormField>
      </FormField>
    </SidePanelSection>
  );
}
